import React, { useRef, useEffect } from "react";
import { Container, Sprite } from "@pixi/react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import LogoController from "./LogoController";
import { safeInit } from "../../../../utilities";

// Device-specific multipliers and position offsets
const DEVICE_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 0.95,
      xOffsetMultiplier: -4.3, // toolbar.width / 2 - symbolWidth * 4
      yOffsetMultiplier: 1.35, // symbolHeight * 1.35
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 0.8,
      xOffsetMultiplier: -4.15,
      yOffsetMultiplier: 1.6,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    deviceMultiplier: 0.9,
    xOffsetMultiplier: -4.3,
    yOffsetMultiplier: 1.25,
  },
};

const LogoLandscape = ({ toolbar, renderMode = "static" }) => {
  const { device, orientation, symbolScale, symbolWidth, symbolHeight } =
    useDimensions();
  const logoControllerRef = useRef(null);
  const containerRef = useRef(null);

  const texture = PIXI.Assets.get(ASSET_NAME.logo_landscape);

  const getDeviceConfig = () => {
    if (device === PixiSettings.DEVICE.DESKTOP) {
      return DEVICE_CONFIG[device];
    }
    return (
      DEVICE_CONFIG[device]?.[orientation] || {
        deviceMultiplier: 1.0,
        xOffsetMultiplier: -4.0,
        yOffsetMultiplier: 1.35,
      }
    );
  };

  const getScaledDimensions = (config) => {
    if (!texture) return { scaledWidth: 0, scaledHeight: 0 };
    const nativeWidth = texture.width;
    const nativeHeight = texture.height;
    const scaledWidth = nativeWidth * config.deviceMultiplier * symbolScale;
    const scaledHeight = nativeHeight * config.deviceMultiplier * symbolScale;
    return { scaledWidth, scaledHeight };
  };

  useEffect(() => {
    // Only create controller if spine mode is enabled
    if (
      renderMode === "spine" &&
      containerRef.current &&
      !logoControllerRef.current &&
      texture
    ) {
      const deviceConfig = getDeviceConfig();
      const { scaledWidth, scaledHeight } = getScaledDimensions(deviceConfig);

      safeInit(() => {
        logoControllerRef.current = new LogoController(
          containerRef.current,
          scaledWidth,
          scaledHeight,
          deviceConfig
        );
        const shouldBeVisible =
          orientation !== PixiSettings.ORIENTATION.PORTRAIT;
        logoControllerRef.current.setVisible(shouldBeVisible);
      });
    }

    return () => {
      if (logoControllerRef.current) {
        logoControllerRef.current.cleanup();
        logoControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texture, renderMode]);

  useEffect(() => {
    if (logoControllerRef.current && texture) {
      const deviceConfig = getDeviceConfig();
      const { scaledWidth, scaledHeight } = getScaledDimensions(deviceConfig);

      // Only update dimensions if spine is initialized
      if (logoControllerRef.current.isInitialized) {
        logoControllerRef.current.updateDimensions(
          scaledWidth,
          scaledHeight,
          deviceConfig
        );
      }

      const shouldBeVisible =
        renderMode === "spine" &&
        orientation !== PixiSettings.ORIENTATION.PORTRAIT;
      logoControllerRef.current.setVisible(shouldBeVisible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    renderMode,
    symbolScale,
    symbolWidth,
    symbolHeight,
    device,
    orientation,
    texture,
  ]);

  if (renderMode === "static") {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return null;
    }
    if (!texture) {
      return null;
    }
  }

  const deviceConfig = getDeviceConfig();

  const getLogoPosition = () => {
    const x = toolbar.width / 2 + symbolWidth * deviceConfig.xOffsetMultiplier;
    const y = symbolHeight * deviceConfig.yOffsetMultiplier;
    return { x, y };
  };

  const logoPosition = getLogoPosition();
  const { scaledWidth, scaledHeight } = getScaledDimensions(deviceConfig);

  return (
    <Container
      ref={containerRef}
      x={logoPosition.x}
      y={logoPosition.y}
      name="logo_landscape"
    >
      {renderMode === "static" && texture && (
        <Sprite
          zIndex={5}
          texture={texture}
          width={scaledWidth}
          height={scaledHeight}
          anchor={0.5}
        />
      )}
    </Container>
  );
};

export default LogoLandscape;
